import {
    Background,
    Controls,
    MiniMap,
    Panel,
    ReactFlow,
    useEdgesState,
    useNodesState,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Portal } from '../../../../../_metronic/partials';
import Loader from '../../../../components/atoms/Loader';
import JourneyBuilderContext from '../../../../components/organisms/JourneyBuilder/JourneyBuilderContext';
import { NODE_TYPES } from '../../../../consts/builder';
import { getArrayIndex } from '../../../../helpers/arr';
import { extractConditionsFromMako } from '../../../../helpers/str';
import { parseMakoToReadable } from '../../../../helpers/utils';
import { updateDesign } from '../../_rego.service';
import EdgeDefault from './DesignEditorEdges/EdgeDefault';
import NodeApiRequest from './DesignEditorNodes/NodeApiRequest';
import NodeEdgeOperation from './DesignEditorNodes/NodeEdgeOperation';
import NodeExpression from './DesignEditorNodes/NodeExpression';
import NodeFlowConnector from './DesignEditorNodes/NodeFlowConnector';
import NodeTask from './DesignEditorNodes/NodeTask';
import NodeWebForm from './DesignEditorNodes/NodeWebForm';
import NodeSettings from './NodeSettings';
import DesignEditorParams from './DesignEditorParams';
import FlowEditorSecrets from '../FlowEditor/FlowEditorSecrets';

const DesignEditor = ({ flow, details }) => {
    const [loading, setLoading] = useState(false);
    const [edit, setEdit] = useState('');
    const HORIZONTAL_SPACING = 200;
    const VERTICAL_SPACING = 80;

    const [nerd, setNerd] = useState(false);
    const [theme, setTheme] = useState('light');

    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);

    useEffect(() => {
        const parsedNodes = parseNodes(details.graph);
        const parsedEdges = parseEdges(details.graph);

        setNodes(parsedNodes);
        setEdges(parsedEdges);
    }, [details]);

    const parseNodes = (graph) => {
        let res = [];
        const visitedNodes = [];

        res = createNode(graph.start_node, res, {
            nodes: graph.nodes,
            visitedNodes,
        });
        return res;
    };

    const createNode = (id, res, config) => {
        const node = config?.nodes?.[id];
        const nodeType =
            Object.keys(NODE_TYPES).indexOf(node?.type) !== -1 ? node?.type : 'default';
        const newNode = buildNode(id, res, {
            ...config,
            nodeType,
        });
        res.push(newNode);
        config.visitedNodes.push(id);
        let edgesCreated = 0;
        config?.nodes?.[id]?.edges?.forEach((edge, edgeIndex) => {
            const target_nodes =
                typeof edge?.target_node === 'object'
                    ? extractConditionsFromMako(edge?.target_node?.template, edge?.condition)
                    : [{ node: edge?.target_node }];

            target_nodes.forEach((target_node) => {
                if (
                    target_node?.node &&
                    config.visitedNodes.indexOf(target_node?.node) === -1 &&
                    config?.nodes?.[target_node?.node]
                ) {
                    res = createNode(target_node?.node, res, {
                        ...config,
                        prevPosition: {
                            x: newNode.position.x,
                            y:
                                (parseInt(newNode?.position?.y) || 0) +
                                (parseInt(edgesCreated ? newNode?.style?.height : 0) || 0) +
                                edgesCreated * VERTICAL_SPACING,
                        },
                        prevStyle: newNode.style,
                    });
                    edgesCreated = edgesCreated + 1;
                }
            });
        });
        return res;
    };

    const buildNode = (id, res, config) => {
        const nodes_positions = details?.builder?.positions || {};

        const x =
            parseInt(config?.prevPosition?.x || 0) +
            parseInt(config?.prevStyle?.width || 0) +
            HORIZONTAL_SPACING;

        const position = nodes_positions?.[id]?.position || {
            x,
            y: config?.prevPosition?.y || 0,
        };

        const style = nodes_positions?.[id]?.style || {
            width: NODE_TYPES?.[config.nodeType].width,
            height: NODE_TYPES?.[config.nodeType].height,
        };

        const measured = nodes_positions?.[id]?.measured;

        return {
            id,
            type: config.nodeType,
            position,
            style,
            measured,
            data: {
                label: id,
                node: config?.nodes?.[id],
                onEdit: () => {
                    setEdit(id);
                },
            },
            sourcePosition: 'right',
        };
    };

    const parseEdges = (graph) => {
        const res = [];
        const visitedEdges = [];
        Object.entries(graph?.nodes)?.forEach(([id, node]) => {
            node?.edges?.forEach((edge) => {
                const target_nodes =
                    typeof edge?.target_node === 'object'
                        ? extractConditionsFromMako(edge?.target_node?.template, edge?.condition)
                        : [{ node: edge?.target_node }];

                target_nodes.forEach((target_node) => {
                    const edgeId = `${id}->${target_node.node}`;
                    let condition = target_node?.condition || edge?.condition;
                    if (
                        target_node.node &&
                        edge?.action !== 'navigate' &&
                        Object.keys(graph.nodes).indexOf(id) !== -1 &&
                        visitedEdges.indexOf(edgeId) === -1
                    ) {
                        visitedEdges.push(edgeId);
                        const newEdge = {
                            id: edgeId,
                            source: id,
                            target: target_node.node,
                            type: 'default',
                            data: { edge },
                        };
                        if (condition) {
                            if (typeof condition === 'object') {
                                condition = condition.template;
                            }
                            newEdge.data.label = {
                                center: parseMakoToReadable(condition),
                            };
                        }
                        res.push(newEdge);
                    }
                });
            });
        });
        return res;
    };

    const handleNodeDragStop = (event, node) => {
        setNodes((prevNodes) => {
            const index = getArrayIndex(prevNodes, 'id', node.id);
            prevNodes[index].position = node.position;
            return prevNodes;
        });
    };

    const handleSave = async () => {
        const positions = {};
        nodes.forEach((node) => {
            positions[node.id] = {
                style: node.style,
                position: node.position,
                measured: node.measured,
            };
        });
        const graph = details.graph;
        graph.positions = positions;
        setLoading(true);
        await updateDesign(details.id, {
            force: true,
            builder: { positions },
        });
        setLoading(false);
        localStorage.setItem('nodes_positions', JSON.stringify(positions));
    };

    return (
        <div style={{ width: 'calc(100vw - 50px)', height: 'calc(100vh - 110px)' }}>
            <Loader loading={loading} />
            {edit && (
                <Portal id='root-drawers'>
                    {details?.graph?.nodes?.[edit] && (
                        <NodeSettings
                            node={details.graph.nodes[edit]}
                            onClose={() => setEdit('')}
                        />
                    )}
                </Portal>
            )}
            <JourneyBuilderContext.Provider value={{ nerd }}>
                <ReactFlow
                    panOnScroll={true}
                    nodes={nodes}
                    edges={edges}
                    colorMode={theme}
                    nodeTypes={{
                        web_form: NodeWebForm,
                        task: NodeTask,
                        api_request: NodeApiRequest,
                        edge_operation: NodeEdgeOperation,
                        expression: NodeExpression,
                        flow_connector: NodeFlowConnector,
                    }}
                    // onConnect={handleConnect}
                    onNodesChange={onNodesChange}
                    onEdgesChange={onEdgesChange}
                    onNodeDragStop={handleNodeDragStop}
                    // onEdgesDelete={handleDeleteEdge}
                    // nodeTypes={nodeTypes}
                    edgeTypes={{ default: EdgeDefault }}>
                    <Background />
                    <Controls />
                    <MiniMap />
                    <Panel position='bottom-center'>
                        <div className='d-flex flex-column justify-content-center align-items-center'>
                            <button
                                className='btn btn-lg btn-success shadow mb-5'
                                onClick={handleSave}>
                                Save Flow
                            </button>
                            <div className='d-flex justify-content-center align-items-center'>
                                <DesignEditorParams params={details.params} />
                                <FlowEditorSecrets secret_id={details.secret} />
                                <OverlayTrigger
                                    placement='top'
                                    overlay={
                                        <Tooltip>{nerd ? 'Disable' : 'Enable'} Nerd Mode</Tooltip>
                                    }>
                                    <button
                                        className={clsx(
                                            `btn btn-icon btn-xs shadow shadow-sm border me-2`,
                                            {
                                                'border-gray-400': !nerd,
                                                'border-dotted': !nerd,
                                                'btn-warning': nerd,
                                                'border-warning': nerd,
                                            }
                                        )}>
                                        {nerd && (
                                            <i
                                                className='ki-duotone ki-ghost fs-3'
                                                onClick={() => setNerd(false)}></i>
                                        )}
                                        {!nerd && (
                                            <i
                                                className='ki-duotone ki-emoji-happy fs-3'
                                                onClick={() => setNerd(true)}></i>
                                        )}
                                    </button>
                                </OverlayTrigger>
                                <div>
                                    <button
                                        className='btn btn-icon btn-xs shadow shadow-sm border border-dotted border-gray-400'
                                        data-kt-menu-trigger='click'
                                        data-kt-menu-placement='bottom-start'>
                                        {theme === 'light' && (
                                            <i className='ki-duotone ki-night-day fs-3'></i>
                                        )}
                                        {theme === 'dark' && (
                                            <i className='ki-duotone ki-moon fs-3'></i>
                                        )}
                                    </button>
                                    <div
                                        className='menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-800 menu-state-bg-light-primary fw-semibold w-200px py-3'
                                        data-kt-menu='true'>
                                        <div
                                            onClick={() => setTheme('light')}
                                            className='menu-item px-3'>
                                            <div className='menu-link px-3'>
                                                <i className='ki-duotone ki-night-day fs-3 me-2'></i>
                                                Light
                                            </div>
                                        </div>
                                        <div
                                            onClick={() => setTheme('dark')}
                                            className='menu-item px-3'>
                                            <div className='menu-link px-3'>
                                                <i className='ki-duotone ki-moon fs-3 me-2'></i>{' '}
                                                Dark
                                            </div>
                                        </div>
                                        <div
                                            onClick={() => setTheme('system')}
                                            className='menu-item px-3'>
                                            <div className='menu-link px-3'>
                                                <i className='ki-duotone ki-screen fs-3 me-2'></i>
                                                System
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Panel>
                </ReactFlow>
            </JourneyBuilderContext.Provider>
        </div>
    );
};
export default DesignEditor;
